// src/components/finance/FinanceJournal.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchFinanceJournal, exportFinance, createFinanceEntry } from '../../api.js'
import FinanceNav from './FinanceNav.jsx'

const JOURNAL_CODES = ['MISC', 'SALES', 'BANK', 'EXP', 'PURCH', 'SERV']

const EMPTY_LINE = { account_code: '', debit: '', credit: '', line_label: '' }
const EMPTY_ENTRY = {
  entry_date: new Date().toISOString().slice(0, 10),
  journal_code: 'MISC',
  memo: '',
  lines: [{ ...EMPTY_LINE }, { ...EMPTY_LINE }],
}

function EntryModal({ onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_ENTRY)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  function setField(key, val) {
    setForm(f => ({ ...f, [key]: val }))
  }

  function setLine(idx, key, val) {
    setForm(f => {
      const lines = f.lines.map((l, i) => i === idx ? { ...l, [key]: val } : l)
      return { ...f, lines }
    })
  }

  function addLine() {
    setForm(f => ({ ...f, lines: [...f.lines, { ...EMPTY_LINE }] }))
  }

  function removeLine(idx) {
    setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }))
  }

  const totalDebit = form.lines.reduce((s, l) => s + (Number(l.debit) || 0), 0)
  const totalCredit = form.lines.reduce((s, l) => s + (Number(l.credit) || 0), 0)
  const balanced = Math.abs(totalDebit - totalCredit) < 0.001

  async function handleSave(e) {
    e.preventDefault()
    if (!balanced) { setErr('Écriture non équilibrée (débit ≠ crédit)'); return }
    setSaving(true)
    setErr(null)
    try {
      const payload = {
        entry_date: form.entry_date,
        journal_code: form.journal_code,
        memo: form.memo || null,
        lines: form.lines
          .filter(l => l.account_code || l.debit || l.credit)
          .map(l => ({
            account_code: l.account_code,
            debit: Number(l.debit) || 0,
            credit: Number(l.credit) || 0,
            line_label: l.line_label || null,
          })),
      }
      await createFinanceEntry(payload)
      onSaved()
    } catch (e) {
      setErr(String(e?.message || e || 'Erreur'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">Nouvelle écriture manuelle</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl">✕</button>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Date</label>
              <input type="date" required value={form.entry_date} onChange={e => setField('entry_date', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 block mb-1">Journal</label>
              <select value={form.journal_code} onChange={e => setField('journal_code', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
                {JOURNAL_CODES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">Libellé / Note</label>
            <input type="text" value={form.memo} onChange={e => setField('memo', e.target.value)}
              placeholder="Description de l'écriture"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-600">Lignes</label>
              <button type="button" onClick={addLine} className="text-xs text-slate-500 hover:text-slate-800 underline">+ Ajouter une ligne</button>
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-1 text-xs font-semibold text-slate-400 px-1">
                <div className="col-span-3">Compte</div>
                <div className="col-span-3">Libellé</div>
                <div className="col-span-2 text-right">Débit</div>
                <div className="col-span-2 text-right">Crédit</div>
                <div className="col-span-2" />
              </div>
              {form.lines.map((line, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-1 items-center">
                  <div className="col-span-3">
                    <input type="text" value={line.account_code} onChange={e => setLine(idx, 'account_code', e.target.value)}
                      placeholder="411, 701…" className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm" />
                  </div>
                  <div className="col-span-3">
                    <input type="text" value={line.line_label} onChange={e => setLine(idx, 'line_label', e.target.value)}
                      placeholder="Libellé" className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm" />
                  </div>
                  <div className="col-span-2">
                    <input type="number" min="0" step="0.01" value={line.debit} onChange={e => setLine(idx, 'debit', e.target.value)}
                      placeholder="0" className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm text-right" />
                  </div>
                  <div className="col-span-2">
                    <input type="number" min="0" step="0.01" value={line.credit} onChange={e => setLine(idx, 'credit', e.target.value)}
                      placeholder="0" className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm text-right" />
                  </div>
                  <div className="col-span-2 flex justify-end">
                    {form.lines.length > 2 && (
                      <button type="button" onClick={() => removeLine(idx)}
                        className="text-red-400 hover:text-red-600 text-xs px-2">✕</button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-6 mt-2 text-sm font-semibold pt-2 border-t border-slate-100">
              <span className="text-slate-600">Débit: {totalDebit.toLocaleString('fr-FR')}</span>
              <span className="text-slate-600">Crédit: {totalCredit.toLocaleString('fr-FR')}</span>
              <span className={balanced ? 'text-emerald-700' : 'text-red-600'}>
                {balanced ? '✅ Équilibrée' : `⚠️ Écart: ${(totalDebit - totalCredit).toLocaleString('fr-FR')}`}
              </span>
            </div>
          </div>

          {err && <div className="text-red-600 text-sm bg-red-50 rounded-lg px-3 py-2">{err}</div>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">
              Annuler
            </button>
            <button type="submit" disabled={saving || !balanced}
              className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-50">
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/**
 * FinanceJournal.jsx (FEDIS)
 * ------------------------------------------------------------
 * Journal comptable (lecture) - "Compta structurée"
 *
 * Objectif:
 *  - Afficher les écritures/événements financiers (journal) sur une période
 *  - Filtres: dates, type, recherche, pagination
 *  - Robuste: si API absente (404) => hint propre, pas de crash
 *  - Export: délègue à exportFinance() (CSV/PDF côté back)
 *
 * API attendue:
 *  - GET /api/finance/journal?from=YYYY-MM-DD&to=YYYY-MM-DD&limit=50&offset=0&type=...&q=...
 *    -> { rows: [], count: number } (ou tableau direct)
 */

function toNum(v, def = 0) {
  const n = Number(v)
  return Number.isFinite(n) ? n : def
}
function isoDate(d) {
  const dt = d instanceof Date ? d : new Date(d)
  if (Number.isNaN(dt.getTime())) return ''
  return dt.toISOString().slice(0, 10)
}
function fmtDateFR(d) {
  if (!d) return '—'
  const dt = new Date(d)
  if (Number.isNaN(dt.getTime())) return '—'
  return dt.toLocaleDateString('fr-FR')
}
function fmtMoney(v) {
  return `${toNum(v, 0).toLocaleString('fr-FR')} XOF`
}
function safeArray(v) {
  return Array.isArray(v) ? v : []
}

const TYPE_OPTIONS = [
  { key: '', label: 'Tous' },
  { key: 'sale', label: 'Vente' },
  { key: 'purchase', label: 'Achat' },
  { key: 'expense', label: 'Dépense' },
  { key: 'payment', label: 'Paiement' },
  { key: 'adjustment', label: 'Ajustement' },
]

function SkeletonRow() {
  return (
    <div className="grid grid-cols-12 gap-3 p-3 border border-slate-200 rounded-lg bg-white">
      <div className="col-span-2 h-4 bg-slate-100 rounded animate-pulse" />
      <div className="col-span-2 h-4 bg-slate-100 rounded animate-pulse" />
      <div className="col-span-6 h-4 bg-slate-100 rounded animate-pulse" />
      <div className="col-span-2 h-4 bg-slate-100 rounded animate-pulse" />
    </div>
  )
}

export default function FinanceJournal() {
  const [showModal, setShowModal] = useState(false)

  // Default: mois courant
  const defaultRange = useMemo(() => {
    const now = new Date()
    const from = new Date(now.getFullYear(), now.getMonth(), 1)
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    return { from: isoDate(from), to: isoDate(to) }
  }, [])

  const [from, setFrom] = useState(defaultRange.from)
  const [to, setTo] = useState(defaultRange.to)
  const [type, setType] = useState('')
  const [q, setQ] = useState('')

  const [limit, setLimit] = useState(50)
  const [offset, setOffset] = useState(0)

  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [hint, setHint] = useState(null)

  const [rows, setRows] = useState([])
  const [count, setCount] = useState(0)

  const page = Math.floor(offset / limit) + 1
  const pages = Math.max(1, Math.ceil((count || 0) / limit))

  const canPrev = offset > 0
  const canNext = offset + limit < count

  const load = useCallback(async () => {
    setLoading(true)
    setErr(null)
    setHint(null)

    try {
      // On appelle l'API standard (finance.api.js)
      const data = await fetchFinanceJournal({ from, to, limit, offset, type, q })

      // Supporte {rows,count} ou [] direct
      const list = safeArray(data?.rows) || safeArray(data)
      const c = Number(data?.count)
      setRows(list)
      setCount(Number.isFinite(c) ? c : list.length)
    } catch (e) {
      const msg = String(e?.message || e || '')
      const is404 =
        e?.status === 404 ||
        /\b404\b/.test(msg) ||
        /Cannot GET/i.test(msg) ||
        /not found/i.test(msg)

      setRows([])
      setCount(0)

      if (is404) {
        setHint('Endpoint manquant: /api/finance/journal (à implémenter côté backend).')
        setErr(null)
      } else {
        setErr(msg || 'Erreur chargement journal.')
      }
    } finally {
      setLoading(false)
    }
  }, [from, to, limit, offset, type, q])

  // Auto-load
  useEffect(() => {
    load()
  }, [load])

  const onExport = useCallback(async (format) => {
    // exportFinance gère le blob + download
    await exportFinance({ from, to, type: 'journal', format })
  }, [from, to])

  return (
    <div className="p-6 space-y-6 min-h-screen">
      {/* Header */}
      <div className="flex flex-wrap gap-3 items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Journal comptable</h1>
          <div className="text-sm text-slate-500 mt-1">
            Période: <span className="font-semibold">{from}</span> → <span className="font-semibold">{to}</span>
            <span className="ml-2 text-xs text-slate-500">• page {page}/{pages}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-50" to="/finance">
              ← Finance
            </Link>
            <Link className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-50" to="/finance/accounts">
              Plan de comptes →
            </Link>
            <Link className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-50" to="/finance/trial-balance">
              Balance →
            </Link>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="px-4 py-2 rounded-lg bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-50"
            disabled={loading}
          >
            + Écriture manuelle
          </button>
          <button
            type="button"
            onClick={() => onExport('csv')}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            disabled={loading}
            title="Exporter le journal (CSV)"
          >
            🧾 Export CSV
          </button>
          <button
            type="button"
            onClick={() => onExport('pdf')}
            className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-950 disabled:opacity-50"
            disabled={loading}
            title="Exporter le journal (PDF)"
          >
            🖨️ Export PDF
          </button>
        </div>
      </div>

      {showModal && (
        <EntryModal
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load() }}
        />
      )}

      <FinanceNav />

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-3">
            <label className="text-xs font-medium text-slate-500 block mb-1">Du</label>
            <input
              type="date"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-slate-300 focus:border-slate-400 pilot-input"
              value={from}
              onChange={(e) => { setFrom(e.target.value); setOffset(0) }}
            />
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-medium text-slate-500 block mb-1">Au</label>
            <input
              type="date"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-slate-300 focus:border-slate-400 pilot-input"
              value={to}
              onChange={(e) => { setTo(e.target.value); setOffset(0) }}
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-medium text-slate-500 block mb-1">Type</label>
            <select
              className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white pilot-input"
              value={type}
              onChange={(e) => { setType(e.target.value); setOffset(0) }}
            >
              {TYPE_OPTIONS.map(o => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-3">
            <label className="text-xs font-medium text-slate-500 block mb-1">Recherche</label>
            <input
              className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-slate-300 focus:border-slate-400 pilot-input"
              value={q}
              onChange={(e) => { setQ(e.target.value); setOffset(0) }}
              placeholder="ref, client, note…"
            />
          </div>

          <div className="md:col-span-1">
            <label className="text-xs font-medium text-slate-500 block mb-1">Limit</label>
            <select
              className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white pilot-input"
              value={String(limit)}
              onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0) }}
            >
              {[25, 50, 100].map(n => <option key={n} value={String(n)}>{n}</option>)}
            </select>
          </div>

          <div className="md:col-span-12 flex flex-wrap gap-2 justify-between mt-2">
            <button
              type="button"
              className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-950 disabled:opacity-50"
              onClick={() => load()}
              disabled={loading}
            >
              {loading ? 'Chargement…' : '🔄 Actualiser'}
            </button>

            <div className="flex gap-2 items-center">
              <button
                type="button"
                className="px-3 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                onClick={() => setOffset(o => Math.max(0, o - limit))}
                disabled={!canPrev || loading}
              >
                ←
              </button>
              <div className="text-sm text-slate-500">
                {count.toLocaleString('fr-FR')} ligne(s)
              </div>
              <button
                type="button"
                className="px-3 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                onClick={() => setOffset(o => o + limit)}
                disabled={!canNext || loading}
              >
                →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error / hint */}
      {err && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800 text-sm">
          <strong>Erreur :</strong> {err}
        </div>
      )}
      {!err && hint && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-900 text-sm">
          <div className="font-semibold">🧩 Journal non branché</div>
          <div className="mt-1">{hint}</div>
          <div className="mt-2 text-xs text-amber-800">
            Conseil back: renvoyer JSON même en 404 (pas HTML "Cannot GET").
          </div>
        </div>
      )}

      {/* List */}
      <div className="space-y-2">
        {loading && (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        )}

        {!loading && !err && rows.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 text-slate-500">
            Aucune ligne sur la période.
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 text-xs text-slate-500 font-semibold">
              <div className="col-span-2">Date</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-6">Libellé / Référence</div>
              <div className="col-span-2 text-right">Montant</div>
            </div>

            <div className="divide-y divide-slate-100">
              {rows.map((r, idx) => {
                const amount = toNum(r.amount ?? r.total ?? r.value)
                const isPos = amount >= 0
                return (
                  <div
                    key={r.id || r.uid || `${r.date || ''}-${idx}`}
                    className="grid grid-cols-12 gap-3 px-4 py-3 hover:bg-slate-50"
                  >
                    <div className="col-span-2 text-sm text-slate-900 whitespace-nowrap">
                      {fmtDateFR(r.date || r.created_at)}
                    </div>
                    <div className="col-span-2 text-sm text-slate-900">
                      <span className="px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-700">
                        {r.type || 'event'}
                      </span>
                    </div>
                    <div className="col-span-6 min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">
                        {r.label || r.description || r.ref || '—'}
                      </div>
                      <div className="text-xs text-slate-500 truncate mt-0.5">
                        {r.counterparty ? `👤 ${r.counterparty}` : ''}
                        {r.order_external_id ? ` • Cmd ${r.order_external_id}` : ''}
                        {r.customer_uid ? ` • Client ${r.customer_uid}` : ''}
                      </div>
                    </div>
                    <div className={`col-span-2 text-right text-sm font-bold whitespace-nowrap ${isPos ? 'text-emerald-700' : 'text-red-700'}`}>
                      {fmtMoney(amount)}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
